import React from 'react';
import type { StandingRow } from 'types/interfaces/play';
import { percent, recordLabel } from '../playFormat';
import './StandingsTable.css';

interface Props {
  rows: StandingRow[];
  /** TO variant adds the deck name under the username (Decision 23) */
  variant?: 'player' | 'host' | 'public';
  subtitle?: string;
}

/**
 * Decision 23: rank, initial circle (viewer row on the gradient), username,
 * W-L-D, points, OMW%. The viewer's row is highlighted; dropped players stay
 * listed with "(dropped)".
 */
export const StandingsTable: React.FC<Props> = ({ rows, variant = 'player', subtitle }) => (
  <section className="play-card play-standings" data-testid="play-standings">
    <div className="play-standings__head">
      <p className="play-eyebrow">Standings</p>
      {subtitle && <span className="play-standings__sub">{subtitle}</span>}
    </div>
    {rows.length === 0 ? (
      <p className="play-helper">Standings appear once players have joined.</p>
    ) : (
      <ol className="play-standings__list">
        <li className="play-standings__row play-standings__row--header" aria-hidden="true">
          <span className="play-standings__rank">#</span>
          <span className="play-standings__who">Player</span>
          <span className="play-standings__rec">W-L-D</span>
          <span className="play-standings__pts">Pts</span>
          <span className="play-standings__omw">OMW%</span>
        </li>
        {rows.map((r) => (
          <li
            key={r.tournamentPlayerId}
            className={`play-standings__row ${r.isViewer ? 'play-standings__row--viewer' : ''} ${r.dropped ? 'play-standings__row--dropped' : ''}`}
            data-testid="play-standings-row"
            aria-current={r.isViewer ? 'true' : undefined}
          >
            <span className="play-standings__rank">{r.rank}</span>
            <span className="play-standings__who">
              <span className={`play-avatar-initial play-avatar-initial--small ${r.isViewer ? 'play-avatar-initial--viewer' : ''}`}>{r.username.slice(0, 1).toUpperCase()}</span>
              <span className="play-standings__names">
                <span className="play-standings__name">
                  {r.username}
                  {r.dropped ? ' (dropped)' : ''}
                </span>
                {variant === 'host' && r.deckName && <span className="play-standings__deck">{r.deckName}</span>}
              </span>
            </span>
            <span className="play-standings__rec">{recordLabel(r.wins, r.losses, r.draws)}</span>
            <span className="play-standings__pts">{r.points}</span>
            <span className="play-standings__omw">{percent(r.omw)}</span>
          </li>
        ))}
      </ol>
    )}
  </section>
);

export default StandingsTable;
